import { type Maybe, rd, type RemoteData } from '@passionware/monads';
import type { Resource } from '__types__/v1/models';
import { MapPin } from 'lucide-react';
import { type ComponentType, type ReactNode, useMemo } from 'react';
import { usePress } from 'react-aria';
import { useTranslation } from 'react-i18next';
import type { WorkspaceScoping } from 'v5/api/_common/query/workspace-scoping';
import type { ResourceSearchQuery } from 'v5/api/resource/resource.api';
import { Avatar, AvatarFallback, AvatarImage } from 'v5/design-system/Avatar';
import { Badge } from 'v5/design-system/Badge';
import { Skeleton } from 'v5/design-system/Skeleton';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { getDimmedContainerClassName } from 'v4/shared/components/forms/input/primitvies/DimmedContainer';
import { DataCard } from 'v5/patterns/collection/DataCard';
import { DragPicture } from 'v5/patterns/dnd/DragPicture';
import { EntityListRowCellErrorBoundary } from 'v5/patterns/entity-list/EntityListRowCellErrorBoundary';
import { EntityListRowValidationIndicator } from 'v5/patterns/entity-list/EntityListRowValidationIndicator';
import { entityListInvalidRowSimpleClassName } from 'v5/patterns/entity-list/entityListInvalidRowChrome';
import { asEntityForUnsafeRender } from 'v5/patterns/entity-list/ValidatedRowInvalidRenderPanel';
import { EntityGridDeferredRowRender } from 'v5/patterns/entity-list/SortableEntityGrid/EntityGridListItem';
import { RowShell } from 'v5/patterns/collection/RowShell';
import {
  TileCollection,
  type TileState
} from 'v5/patterns/collection/TileCollection';
import {
  SkeletonLine,
  SkeletonTile,
  TileCollectionSkeleton
} from 'v5/patterns/collection/TileCollectionSkeleton';
import { TileShell } from 'v5/patterns/collection/TileShell';
import {
  virtualGridLayout,
  virtualListLayout
} from 'v5/patterns/collection/virtualLayouts';
import { cn } from 'v5/platform/dom/cn';
import type { ZodError } from 'zod';
import { type ListSlot, pagedList } from 'v5/platform/lang/pagedList';
import { PlaceReport } from 'v5/platform/react/PlaceReport';
import { useRenderedPages } from 'v5/platform/react/useRenderedPages';
import type { WithServices } from 'v5/platform/ts/services';
import {
  EntityHoverCard,
  type EntityCardData
} from 'v5/patterns/widgets/EntityHoverCard';
import type { MediaPin } from 'v5/patterns/widgets/MediaCarousel/mediaCarousel.types';
import type { WithResourceService } from 'v5/services/io/ResourceService/ResourceService';
import { formatRateSummary } from 'v1/helpers/budgetHelper';
import { ResourcePinStrip } from './ResourcePinStrip';
import { ResultBadges } from './ResultBadges';
import { ResultPictures } from './ResultPictures';
import { resourceDrag } from './resourceDrag';
import { resourceRow } from './resourceRow';
import { scopedResourceQuery } from './scopedResourceQuery';

/** Results read per page; a few screens of the densest view. */
export const RESULTS_PAGE_SIZE = 50;

/** Computed, never measured: the list is virtualized over every result. */
const ROW_HEIGHT_PX = 56;
/** A tile at least this wide, the columns stretched to the row, its proportion kept. */
const TILE_MIN_WIDTH_PX = 150;
const TILE_HEIGHT_PER_WIDTH = 236 / 150;
const TILE_GAP_PX = 10;

export type ResourcePickerView = 'list' | 'gallery';

/** What only the host knows of a resource: how its card reads, where it lives, its pictures. */
export interface ResourcePickerSource {
  cardOf: (resource: Resource) => EntityCardData;
  pageHrefOf: (resource: Resource) => string;
  usePins: (resourceId: number) => RemoteData<MediaPin[]>;
}

export interface ResourcePickerResultsProps
  extends WithServices<[WithResourceService]> {
  query: Maybe<ResourceSearchQuery>;
  /** Whose resources are searched (see `scopedResourceQuery`); absent while not known yet. */
  scope: Maybe<WorkspaceScoping>;
  view: ResourcePickerView;
  source: ResourcePickerSource;
  /** Open a result beside the list: its details, or its pictures (at the one pressed). */
  onOpen: (
    resource: Resource,
    tab: 'details' | 'pinboard',
    pinId?: number
  ) => void;
  /** The result open beside the list, marked in it. */
  openId?: number | null;
  /**
   * Picking several results with the round check (Shift for a range), for the host to act on
   * them together. Without it the results have no check.
   */
  selection?: {
    ids: ReadonlySet<number>;
    onChange: (ids: Set<number>) => void;
    label: string;
  };
  /** Results the host has already (on the shortlist): marked, and not picked again. */
  present?: { ids: ReadonlySet<number>; label: string };
  /** Results can be dragged out (`resourceDrag`): the picked ones, or the one dragged. */
  draggable?: boolean;
  /** The host's own marks on a result (a booking conflict): before the locations, or on the picture. */
  marksOf?: (resource: Resource) => ReactNode;
  /**
   * What a result says of itself, chosen by the host (the user's result fields): the line under
   * the name and the values shown as badges. Without it, role · rate and the locations.
   */
  describe?: (resource: Resource) => ResultDescription;
  emptyState: ReactNode;
}

export interface ResultDescription {
  /** Under the name, in the list and on a tile. */
  line: string;
  /** Badges on the right of a row, one list after another, each its first value and "+N". */
  badges: {
    key: string;
    values: string[];
    icon: ComponentType<{ className?: string }> | null;
  }[];
}

/**
 * A result: the resource, with the issues when it failed validation (then its unvalidated data);
 * `resource` is null for a row that failed with no data to show, which keeps its id and place.
 */
interface PickedResource {
  id: number;
  resource: Resource | null;
  error?: ZodError;
}

interface Item {
  id: string;
  index: number;
  slot: ListSlot<PickedResource>;
}

/**
 * Every result of a search as one continuous list or gallery, its scrollbar the whole length from
 * the start: no pages to turn, no "load more". Pages are read for the results on screen only, and
 * one not read yet is a placeholder of the same size. Both views are layouts of one collection, so
 * the picks and the focus stay when the view changes. A press on a result picks it (Shift for a
 * range, a rectangle across them); its name opens it; it drags out (`resourceDrag`).
 */
export function ResourcePickerResults({
  query,
  scope,
  view,
  source,
  onOpen,
  openId,
  selection,
  present,
  draggable = false,
  marksOf,
  describe,
  emptyState,
  services
}: ResourcePickerResultsProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  const { pages, report } = useRenderedPages(RESULTS_PAGE_SIZE);
  // the pictures are the search's to bring (`includePinsPreview`): their squares wait only then
  const pictures = query?.includePinsPreview === true;
  const pageResults = services.resourceService.useListPages(
    scopedResourceQuery(query, scope),
    pages,
    RESULTS_PAGE_SIZE
  );
  const validPages = pageResults.map(page =>
    rd.map(page, loaded => ({
      // a row that failed validation keeps its place, shown from its unvalidated data as the
      // resource list shows it; with no data it stays a failed row
      items: loaded.items.map(
        (row): PickedResource =>
          row.isValid
            ? { id: row.id, resource: row.data }
            : {
                id: row.id,
                resource:
                  row.rawItem === undefined
                    ? null
                    : asEntityForUnsafeRender<Resource>(row.rawItem),
                error: row.error
              }
      ),
      count: loaded.count
    }))
  );
  const failed = validPages.find(page => rd.isError(page));
  const freshCount = pagedList.countOf(validPages);
  // a new search keeps the last results on screen, dimmed, until its own count comes; pages read
  // while scrolling are slots, not a new search
  const results = rd.useLastWithPlaceholder(
    freshCount !== null
      ? rd.of({
          count: freshCount,
          pageMap: new Map(pages.map((page, i) => [page, validPages[i]]))
        })
      : failed && rd.isError(failed)
        ? rd.ofError(failed.error)
        : rd.ofPending()
  );
  const isPlaceholder = rd.isPlaceholderData(results);
  const { count, pageMap } = rd.tryGet(results) ?? {
    count: null,
    pageMap: new Map()
  };
  const layout = useMemo(
    () =>
      view === 'list'
        ? virtualListLayout({ rowHeight: ROW_HEIGHT_PX })
        : virtualGridLayout({
            tileMinWidth: TILE_MIN_WIDTH_PX,
            tileHeight: width => width * TILE_HEIGHT_PER_WIDTH,
            gap: TILE_GAP_PX
          }),
    [view]
  );

  if (rd.isError(results)) {
    return <p className="p-6 text-sm text-fg-danger">{t('loadFailed')}</p>;
  }
  // only the first search has nothing to show meanwhile
  if (count === null) {
    return (
      <div className="flex min-h-0 flex-1 flex-col">
        <TileCollectionSkeleton
          label={t('loading')}
          layout={layout}
          className="flex min-h-0 flex-1 flex-col"
          renderTile={tile => (
            <ResultSkeleton
              tile={tile}
              view={view}
              pictures={pictures}
              picks={selection !== undefined}
            />
          )}
        />
      </div>
    );
  }
  if (count === 0) return <>{emptyState}</>;

  const items: Item[] = Array.from({ length: count }, (_, index) => {
    const slot = pagedList.slotAt(index, RESULTS_PAGE_SIZE, pageMap);
    return {
      id: slot.type === 'item' ? String(slot.item.id) : `slot-${index}`,
      index,
      slot
    };
  });
  const isPresent = (resource: Resource) =>
    present?.ids.has(resource.id) === true;
  // as the media field's tiles drag: the first ones' pictures stacked, and how many
  const dragLayer = (resource: Resource) =>
    resource.profile_picture ? (
      <DragPicture src={resource.profile_picture} />
    ) : (
      <span className="grid size-full place-items-center text-2xl font-semibold text-fg-muted">
        {resourceRow.initials(resource)}
      </span>
    );

  return (
    <div
      aria-busy={isPlaceholder || undefined}
      className={cn(
        'flex min-h-0 flex-1 flex-col',
        getDimmedContainerClassName(isPlaceholder)
      )}
    >
      <TileCollection
        label={t('results')}
        items={items}
        keyOf={item => item.id}
        textOf={item =>
          item.slot.type === 'item' && item.slot.item.resource
            ? (resourceRow.name(item.slot.item.resource) ?? t('unnamed'))
            : t('loading')
        }
        layout={layout}
        // not read yet, or already the host's: not picked, still opened
        pickable={item =>
          item.slot.type === 'item' &&
          item.slot.item.resource !== null &&
          !isPresent(item.slot.item.resource)
        }
        selection={
          selection && {
            selected: new Set([...selection.ids].map(String)),
            onChange: keys =>
              selection.onChange(
                new Set(
                  [...keys].filter(key => !key.startsWith('slot-')).map(Number)
                )
              ),
            // Cmd+A picks the results read so far that can be picked: the rest aren't there yet
            marquee: true
          }
        }
        outgoing={
          draggable
            ? {
                itemOf: item => {
                  const resource =
                    item.slot.type === 'item' ? item.slot.item.resource : null;
                  return resource
                    ? (resourceDrag.itemsOf([
                        {
                          id: resource.id,
                          name: resourceRow.name(resource) ?? ''
                        }
                      ])[0] ?? null)
                    : null;
                },
                dragLayer: item =>
                  item.slot.type === 'item' && item.slot.item.resource
                    ? dragLayer(item.slot.item.resource)
                    : null
              }
            : undefined
        }
        className="flex min-h-0 flex-1 flex-col"
        renderTile={(item, tile) => {
          const resource =
            item.slot.type === 'item' ? item.slot.item.resource : null;
          const shared = {
            item,
            tile,
            pictures,
            report,
            source,
            onOpen,
            isOpen: resource !== null && resource.id === openId,
            checkLabel: selection?.label ?? '',
            presentLabel:
              resource && isPresent(resource) ? present?.label : undefined,
            marksOf,
            describe
          };
          return view === 'list' ? (
            <ResultRow {...shared} />
          ) : (
            <ResultTile {...shared} />
          );
        }}
      />
    </div>
  );
}

interface ResultProps {
  item: Item;
  tile: TileState;
  pictures: boolean;
  report: (index: number) => () => void;
  source: ResourcePickerSource;
  onOpen: ResourcePickerResultsProps['onOpen'];
  isOpen: boolean;
  checkLabel: string;
  /** Already the host's: the grey check, ticked for good, saying so. */
  presentLabel: string | undefined;
  marksOf: ResourcePickerResultsProps['marksOf'];
  describe: ResourcePickerResultsProps['describe'];
}

/** A result in the list: the row the kit draws, its body the picker's. */
function ResultRow({
  item,
  tile,
  pictures,
  report,
  source,
  onOpen,
  isOpen,
  checkLabel,
  presentLabel,
  marksOf,
  describe
}: ResultProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  const { t: tRow } = useTranslation('patterns', {
    keyPrefix: 'EntityListRow'
  });
  const resource = item.slot.type === 'item' ? item.slot.item.resource : null;
  const error = item.slot.type === 'item' ? item.slot.item.error : undefined;
  const name = resource
    ? (resourceRow.name(resource) ?? t('unnamed'))
    : t('loading');
  return (
    <RowShell
      tile={tile}
      selectLabel={checkLabel}
      dragLabel={t('drag')}
      current={isOpen}
      locked={presentLabel}
      className={cn(
        '@container/result',
        error && entityListInvalidRowSimpleClassName
      )}
    >
      <PlaceReport index={item.index} report={report} />
      {resource === null ? (
        item.slot.type !== 'loading' ? (
          <span className="px-1 text-sm text-fg-danger">{t('rowFailed')}</span>
        ) : (
          <RowBodySkeleton pictures={pictures} />
        )
      ) : (
        <EntityListRowCellErrorBoundary
          labels={{
            title: tRow('renderCrashed'),
            tryAgain: tRow('renderTryAgain')
          }}
        >
          <EntityGridDeferredRowRender
            render={() => {
              const description = describe?.(resource);
              return (
                <>
                  <Avatar size="sm">
                    {resource.profile_picture ? (
                      <AvatarImage src={resource.profile_picture} alt="" />
                    ) : null}
                    <AvatarFallback className="text-xs font-semibold">
                      {resourceRow.initials(resource)}
                    </AvatarFallback>
                  </Avatar>
                  {/* the name and its line keep their width down to 120px; the badges give way first */}
                  <div className="flex min-w-[120px] flex-initial flex-col">
                    <ResultName
                      resource={resource}
                      name={name}
                      source={source}
                      onOpen={onOpen}
                    />
                    <span className="truncate text-sm text-fg-muted">
                      {description ? description.line : secondLine(resource, t)}
                    </span>
                  </div>
                  {error && (
                    <EntityListRowValidationIndicator
                      error={error}
                      rawItem={resource}
                    />
                  )}
                  <ResultBadges
                    lists={
                      description?.badges ?? [
                        {
                          key: 'locations',
                          values: resourceRow.locations(resource),
                          icon: MapPin
                        }
                      ]
                    }
                  />
                  {marksOf?.(resource)}
                  {/* last: the strips of every row stand in one column at the edge */}
                  <ResultPictures.Strip
                    resource={resource}
                    name={name}
                    onOpen={pinId => onOpen(resource, 'pinboard', pinId)}
                  />
                </>
              );
            }}
          />
        </EntityListRowCellErrorBoundary>
      )}
    </RowShell>
  );
}

/** A result in the gallery: the tile the kit draws, its card the picker's pictures and name. */
function ResultTile({
  item,
  tile,
  pictures,
  report,
  source,
  onOpen,
  isOpen,
  checkLabel,
  presentLabel,
  marksOf,
  describe
}: ResultProps) {
  const { t } = useTranslation('patterns', { keyPrefix: 'ResourcePicker' });
  const { t: tRow } = useTranslation('patterns', {
    keyPrefix: 'EntityListRow'
  });
  const resource = item.slot.type === 'item' ? item.slot.item.resource : null;
  const error = item.slot.type === 'item' ? item.slot.item.error : undefined;
  const name = resource
    ? (resourceRow.name(resource) ?? t('unnamed'))
    : t('loading');

  return (
    <TileShell
      tile={tile}
      selectLabel={checkLabel}
      current={isOpen}
      locked={presentLabel}
      attention={error !== undefined}
      // the grid measures a drawn tile: it keeps the proportion itself
      className="aspect-[150/236]"
    >
      {presentLabel !== undefined && (
        <div
          data-tile-mark
          className="pointer-events-none absolute top-2 right-2 z-10"
        >
          <Badge variant="gray" tone="subtle" size="sm">
            {t('added')}
          </Badge>
        </div>
      )}
      <DataCard still={presentLabel !== undefined}>
        <PlaceReport index={item.index} report={report} />
        {resource === null ? (
          item.slot.type !== 'loading' ? (
            <span className="p-2 text-sm text-fg-danger">{t('rowFailed')}</span>
          ) : (
            <TileBodySkeleton pictures={pictures} />
          )
        ) : (
          <EntityListRowCellErrorBoundary
            density="default"
            labels={{
              title: tRow('renderCrashed'),
              tryAgain: tRow('renderTryAgain')
            }}
          >
            <EntityGridDeferredRowRender
              render={() => {
                const description = describe?.(resource);
                return (
                  <div className="flex min-h-0 flex-1 flex-col gap-1.5 p-1.5">
                    <ResultPictures.Tile
                      resource={resource}
                      name={name}
                      onOpen={pinId => onOpen(resource, 'pinboard', pinId)}
                    >
                      {marksOf && (
                        <div className="absolute bottom-1.5 left-1.5 flex max-w-[calc(100%-3rem)] flex-wrap gap-1">
                          {marksOf(resource)}
                        </div>
                      )}
                    </ResultPictures.Tile>
                    {error ? (
                      <div className="flex min-w-0 items-start gap-1.5">
                        <EntityListRowValidationIndicator
                          error={error}
                          rawItem={resource}
                        />
                        <ResultName
                          resource={resource}
                          name={name}
                          source={source}
                          onOpen={onOpen}
                        />
                      </div>
                    ) : (
                      <ResultName
                        resource={resource}
                        name={name}
                        source={source}
                        onOpen={onOpen}
                      />
                    )}
                    <span className="-mt-1.5 truncate text-xs text-fg-muted">
                      {description
                        ? description.line
                        : resource.role?.trim() || t('noRole')}
                    </span>
                  </div>
                );
              }}
            />
          </EntityListRowCellErrorBoundary>
        )}
      </DataCard>
    </TileShell>
  );
}

/** The name, with the resource's card on hover; a press opens the result as the row does. */
function ResultName({
  resource,
  name,
  source,
  onOpen
}: {
  resource: Resource;
  name: string;
  source: ResourcePickerSource;
  onOpen: ResourcePickerResultsProps['onOpen'];
}) {
  // a press of its own: React Aria's row press would pick the result as well
  const { pressProps } = usePress({
    onPress: () => onOpen(resource, 'details')
  });
  return (
    <EntityHoverCard
      card={rd.of(source.cardOf(resource))}
      onOpen={() => onOpen(resource, 'details')}
      pageHref={source.pageHrefOf(resource)}
      media={
        <PinStrip
          resourceId={resource.id}
          source={source}
          onOpen={() => onOpen(resource, 'pinboard')}
        />
      }
    >
      <button
        type="button"
        {...pressProps}
        className={cn(
          'min-w-0 truncate self-start rounded-sm text-left text-sm font-medium hover:underline',
          focusRings.focus.visible
        )}
      >
        {name}
      </button>
    </EntityHoverCard>
  );
}

/** Its own component: the pins are read only while the card is open. */
function PinStrip({
  resourceId,
  source,
  onOpen
}: {
  resourceId: number;
  source: ResourcePickerSource;
  onOpen: () => void;
}) {
  return <ResourcePinStrip pins={source.usePins(resourceId)} onOpen={onOpen} />;
}

function secondLine(
  resource: Resource,
  t: (key: 'noRole' | 'noRate') => string
) {
  const rate = resourceRow.firstRate(resource);
  const rateText = rate
    ? formatRateSummary(
        {
          amount: rate.amount,
          currency: rate.currency,
          interval: rate.interval,
          quantity: 1
        },
        rate.currency || 'USD',
        false
      )
    : null;
  return `${resource.role?.trim() || t('noRole')} · ${rateText || t('noRate')}`;
}

/** A row's body while it is read: the avatar, the name and its line, the pictures' squares. */
function RowBodySkeleton({ pictures }: { pictures: boolean }) {
  return (
    <>
      <Skeleton className="size-8 shrink-0 rounded-md" />
      <div className="flex min-w-0 flex-1 flex-col">
        <SkeletonLine className="text-sm" width="w-36 max-w-full" />
        <SkeletonLine className="text-sm" width="w-24 max-w-full" />
      </div>
      {pictures && <ResultPictures.StripSkeleton />}
    </>
  );
}

/** A tile's body while it is read: the picture, the strip's squares, the name and its line. */
function TileBodySkeleton({ pictures }: { pictures: boolean }) {
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-1.5 p-1.5">
      <Skeleton className="min-h-0 flex-1 rounded-md" />
      {pictures && <ResultPictures.TileStripSkeleton />}
      <SkeletonLine className="text-sm" />
      <SkeletonLine className="-mt-1.5 text-xs" width="w-1/2" />
    </div>
  );
}

/** A placeholder of the first search in the kit's skeleton: the same shell, the same body. */
function ResultSkeleton({
  tile,
  view,
  pictures,
  picks
}: {
  tile: TileState;
  view: ResourcePickerView;
  pictures: boolean;
  picks: boolean;
}) {
  return view === 'list' ? (
    <RowShell
      tile={tile}
      selectLabel=""
      dragLabel=""
      className="@container/result"
    >
      {picks && <Skeleton className="size-6 shrink-0 rounded-full" />}
      <RowBodySkeleton pictures={pictures} />
    </RowShell>
  ) : (
    <SkeletonTile tile={tile} className="aspect-[150/236]">
      <DataCard>
        <TileBodySkeleton pictures={pictures} />
      </DataCard>
    </SkeletonTile>
  );
}
